/**
 * WordpressRegister.js — the WordPress plugin onboarding, mounted at /wordpress-register.
 *
 * STRUCTURE AND STYLING ARE THE WIX ONBOARDING (WixOnboarding.js / wxo-* CSS), step for
 * step, so a WordPress owner sees the same product as a Wix owner:
 *   0. Account  — create account or log in (identity pre-filled from the connect code)
 *   1. Connect  — optional URL (pre-filled) + documents. We INSTANTLY create a ready bot
 *                 (no scrape) and go straight on; the website is learned in the
 *                 BACKGROUND (find_new_links -> scrape_link) with a docked progress strip.
 *   2. Design   — customize the widget (header, welcome, colors, icon) + preview
 *   3. Test     — chat with the agent live
 *   4. Notifications — events x channels matrix + email recipients
 *   5. Pricing  — plan cards + free trial, or continue without a plan
 *
 * WHAT DIFFERS FROM WIX (platform mechanics, not design):
 * - Entry is the plugin's one-time ?connect=<code>, exchanged server-side for the site
 *   identity (no PII in the URL). Legacy query params still work for plugin <= 1.0.6.
 * - The site identity is a wordpress_secrets row (site_id + wordpress_secret_id), not a
 *   Wix app instance, so the bot is created via wordpress/onboarding/create_bot.
 * - There is no sub-app detection (WordPress has no Wix-style apps), so step 1 has no
 *   skill-picker reveal: build -> straight to Design.
 * - Billing is STRIPE, never an app market: the plan cards open create-checkout-session
 *   in a new tab and this tab polls the bot's plan until it flips off "Free".
 * - wordpress/activate_chatbot binds the bot to the plugin and pushes the snippet. It
 *   runs at the EARLIEST possible point (bot + site id known), not at the end, so an
 *   owner who abandons the wizard still has a live agent on their site.
 *
 * Styling: WixOnboarding.css (wxo-/rk- scoped, shared 1:1 with the Wix flow). No "chatbot".
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TextField, Checkbox, IconButton, InputAdornment, Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight, faArrowLeft, faArrowUp, faCheck, faXmark, faGlobe, faUpload, faTrash,
  faFileArrowUp, faFileLines, faPlus, faEllipsis, faHeadset, faEnvelope, faDesktop,
  faComments, faUserPlus, faTriangleExclamation, faBookOpen, faPuzzlePiece, faSliders,
  faMinus, faCode, faLink, faBolt,
} from '@fortawesome/free-solid-svg-icons';
import { faTelegram } from '@fortawesome/free-brands-svg-icons';
import ReactMarkdown from 'react-markdown';
import ChatModal from '../components/chatModal.js';
import WixAuthVisual, { GENERIC_ACTIONS, GENERIC_TOOLS } from '../components/WixAuthVisual.js';
import UploadFileModal from '../components/uploadFileModal.js';
import SlackLogo from '../components/SlackLogo.js';
import { SlackBrandIcon, TeamsBrandIcon, TelegramBrandIcon } from '../components/BrandIcons';
import { CHANNEL_GUIDES } from '../data/channelGuides.js';
import { saveInteraction } from '../components/Interaction';
import { SUPPORTED_LANGUAGES } from '../translations.js';
import { authFetch } from '../utils/api';
import './WixOnboarding.css';

const API = process.env.REACT_APP_API_BASE_URL;

const FIELD_SX = {
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': { border: '1px solid #515151' },
  '& .MuiInputLabel-root': { color: 'gray' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#5616ea' },
};

const DEFAULT_WIDGET = {
  themeColor: '#5616ea',
  headerColor: '#ffffff',
  hoverColor: '#4413c4',
  widgetHorizontalAlignment: 'right',
  widgetSize: 62,
  widgetBorderRadius: 50,
  headerText: 'Chat with us',
  welcomeMessage: 'Hi, how can I assist you?',
  pulsing: true,
  icon: '',   // widget launcher icon (widget_icon_path)
  logo: '',   // logo shown in the chat header (header_icon_path)
  avatar: '', // avatar shown next to agent replies (avatar_icon_path)
};

// Completion strips auto-dismiss this long after finishing, with a depleting ring.
const DONE_HIDE_MS = 5000;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ── Step 4 — notification matrix (events x channels) + email recipients.
//    Same model as the Wix onboarding. ──
const WPO_NOTIFY_CHANNELS = [
  { key: 'in_app', icon: faDesktop, labelKey: 'wxo_notif_ch_inapp' },
  { key: 'email', icon: faEnvelope, labelKey: 'wxo_notif_ch_email' },
];
const WPO_NOTIFY_EVENTS = [
  { key: 'new_chat', section: 'live_chat', icon: faComments, titleKey: 'wxo_notif_chat_title', descKey: 'wxo_notif_chat_desc' },
  { key: 'help_requested', section: 'live_chat', icon: faHeadset, titleKey: 'wxo_notif_help_title', descKey: 'wxo_notif_help_desc', highlight: true },
  { key: 'new_lead', section: 'leads', icon: faUserPlus, titleKey: 'wxo_notif_lead_title', descKey: 'wxo_notif_lead_desc' },
  { key: 'new_message', section: 'live_chat', icon: faEnvelope, titleKey: 'wxo_notif_msg_title', descKey: 'wxo_notif_msg_desc' },
];
const WPO_LOCKED_CHANNELS = { new_message: ['email'] }; // joined-chat replies: in-app only, no email spam
const wpoChannelLocked = (eventKey, channelKey) => (WPO_LOCKED_CHANNELS[eventKey] || []).includes(channelKey);
const WPO_DEFAULT_PREFS = {
  new_chat: { in_app: true, email: false },
  help_requested: { in_app: true, email: true },
  new_lead: { in_app: true, email: true },
  new_message: { in_app: true, email: false },
};

// ── Step 5 — pricing. EVERYTHING shown (names, prices, limits, rescraping, branding,
//    AND the Stripe price id used for checkout) comes live from bot_subscription_plans
//    via /api/subscription_plans — the same source the portal /billing page reads.
//    Nothing is hardcoded. WordPress has NO app-market billing path, so the CTA opens
//    the normal Stripe checkout. Only presentation order + featured tier live in code
//    (as on /billing), since the DB has no order/featured column. ──
const WPO_TIER_ORDER = ['Smart', 'Boost', 'Ultimo'];
const WPO_FEATURED_TIER = 'Boost';

// Apps + Channels logo rows for the plan cards — same set as the Wix onboarding and
// the /billing page, so the cards read identically across surfaces.
const WPO_LOGOS_SHOWN = 5;
const WPO_APPS = [
  { key: 'calendly', label: 'Calendly', logo: '/calendly-logo.png' },
  { key: 'hubspot', label: 'HubSpot', logo: '/hubspot-logo.png' },
  { key: 'stripe', label: 'Stripe', logo: '/stripe-logo.png' },
  { key: 'mailchimp', label: 'Mailchimp', logo: '/mailchimp-logo.png' },
  { key: 'calcom', label: 'Cal.com', logo: '/calcom-logo.png' },
  { key: 'zendesk', label: 'Zendesk', logo: '/zendesk-logo.png' },
];
const WPO_PRIORITY_CHANNELS = [
  { key: 'instagram', label: 'Instagram', logo: '/instagram-logo.webp' },
  { key: 'facebook', label: 'Facebook', logo: '/facebook-logo.webp' },
  { key: 'whatsapp', label: 'WhatsApp', logo: '/whatsapp-logo.webp' },
  { key: 'slack', label: 'Slack', logoComponent: SlackLogo },
  { key: 'telegram', label: 'Telegram', icon: faTelegram, color: '#229ED9' },
  { key: 'website', labelKey: 'billing_channel_website_widget', icon: faCode },
  { key: 'sharelink', labelKey: 'billing_channel_chat_link', icon: faLink },
];
// Website builders / CMS — WordPress first here, since it's the owner's own platform.
const WPO_SITE_PLATFORMS = [
  { key: 'wordpress', label: 'WordPress', logo: '/wordpress-logo.png' },
  { key: 'wix', label: 'Wix', logo: '/wix-logo.png' },
  { key: 'webflow', label: 'Webflow', logo: '/webflow-logo.png' },
];
const WPO_GUIDE_CHANNELS = CHANNEL_GUIDES.map((g) => ({ key: g.key, label: g.name, logo: g.logo }));
const WPO_CHANNELS = [...WPO_PRIORITY_CHANNELS, ...WPO_SITE_PLATFORMS, ...WPO_GUIDE_CHANNELS];
const WPO_CHANNEL_ANY = { key: 'any-web', icon: faCode, labelKey: 'billing_chip_any_website', color: '#5616ea' };
const WPO_APP_ANY = { key: 'any-app', icon: faBolt, labelKey: 'billing_chip_any_app', color: '#5616ea' };

function WpoLogoChip({ item }) {
  const { t } = useTranslation();
  const label = item.labelKey ? t(item.labelKey) : item.label;
  return (
    <span className="rk-logo" data-tip={label} aria-label={label}>
      {item.logoComponent ? (
        <item.logoComponent size={19} />
      ) : item.logo ? (
        <img src={item.logo} alt={label} />
      ) : (
        <FontAwesomeIcon icon={item.icon} style={item.color ? { color: item.color } : undefined} />
      )}
    </span>
  );
}

// A row of the most important logos with a "+N" chip. The popover (listing only the
// hidden items) opens on hover OR click; a small close delay lets the mouse travel
// chip -> popover without flicker.
function WpoLogoRow({ items, lead, max = WPO_LOGOS_SHOWN }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const shown = items.slice(0, max);
  const extra = items.length - max;
  const hidden = items.slice(max);
  const cancelClose = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const scheduleClose = () => { cancelClose(); timer.current = setTimeout(() => setOpen(false), 90); };
  useEffect(() => () => cancelClose(), []);
  return (
    <div className="rk-logo-row">
      <div className="rk-logos">
        {lead && <WpoLogoChip key={lead.key} item={lead} />}
        {shown.map((it) => <WpoLogoChip key={it.key} item={it} />)}
        {extra > 0 && (
          <span className="rk-logo-more-wrap">
            <button
              type="button"
              className="rk-logo-more"
              onMouseEnter={() => { cancelClose(); setOpen(true); }}
              onMouseLeave={scheduleClose}
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              aria-label={t('billing_show_n_more', { count: extra })}
            >
              +{extra}
            </button>
            {open && (
              <div className="rk-logo-pop" role="tooltip" onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
                {hidden.map((it) => (
                  <span key={it.key} className="rk-logo-pop-item">
                    <WpoLogoChip item={it} />
                    <span className="rk-logo-pop-label">{it.labelKey ? t(it.labelKey) : it.label}</span>
                  </span>
                ))}
              </div>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

function base64ToFile(dataUrl, filename) {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8 = new Uint8Array(n);
  while (n--) u8[n] = bstr.charCodeAt(n);
  return new File([u8], filename, { type: mime });
}

// WordPress bills through Stripe, so the card shows the plain monthly_price (the
// wix_monthly_price / shopify_monthly_price columns are for those app markets).
function stripeDisplayPrice(plan) {
  return Number(plan?.monthly_price) || 0;
}

export default function WordpressRegister({ onSetupComplete }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLanguage = (i18n.language || 'en').split('-')[0];
  const currentLangLabel = (SUPPORTED_LANGUAGES.find((l) => l.code === currentLanguage) || { label: 'English' }).label;

  const [step, setStep] = useState(0);
  const TOTAL_STEPS = 6; // account, connect, design, test, notifications, pricing

  const [authMode, setAuthMode] = useState('register');
  const [firstName, setFirstName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [uid, setUid] = useState(null);
  const [token, setToken] = useState(null);

  // WordPress site identity (from the plugin's connect code). `siteUrl` is the
  // PLUGIN-reported site and is what activate_chatbot binds/pushes to; `url` is the
  // editable TRAINING url and may legitimately point at a different site.
  const [siteId, setSiteId] = useState('');
  const [siteUrl, setSiteUrl] = useState('');
  const [wordpressSecretId, setWordpressSecretId] = useState(null);

  const [url, setUrl] = useState('');
  const [docFiles, setDocFiles] = useState([]);   // [{ name, url, size }] (ingested on build)
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [docSync, setDocSync] = useState({ active: false, phase: 'idle', done: 0, total: 0 });
  const [bot, setBot] = useState(null);
  const [pushFailed, setPushFailed] = useState(false);

  // step 4 — notification preferences
  const [notifyPrefs, setNotifyPrefs] = useState(WPO_DEFAULT_PREFS);
  const [notificationEmails, setNotificationEmails] = useState(['']);
  const [notifySaveAttempted, setNotifySaveAttempted] = useState(false);

  // step 5 — pricing / free trial (Stripe checkout, then poll the bot's plan)
  const [billingCycle, setBillingCycle] = useState('YEAR');
  const [isCheckingPlan, setIsCheckingPlan] = useState(false);
  const [planCheckError, setPlanCheckError] = useState('');
  const [trialStarted, setTrialStarted] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  // background website training
  const [training, setTraining] = useState({ active: false, phase: 'idle', found: 0, done: 0 });
  const docKnowledgeRef = useRef(new Set());
  const notifyEmailUserEditedRef = useRef(false);
  const planPollRef = useRef(null);
  const trainingReqRef = useRef(null);
  const trainingKnowledgeRef = useRef(new Set());
  const trainedBotsRef = useRef(new Set());  // agents whose website run already started
  const activatedRef = useRef(false);   // guards the one-shot plugin activation
  // Business context is generated ONCE, after every ingest source finished.
  const bizContextFiredRef = useRef(false);
  const srcExpectedRef = useRef(new Set());
  const srcDoneRef = useRef(new Set());

  // design
  const [widget, setWidget] = useState(DEFAULT_WIDGET);
  const iconInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const avatarInputRef = useRef(null);
  const widgetFetchedRef = useRef(false);

  // design — interactive preview chat
  const [previewInput, setPreviewInput] = useState('');
  const [previewMsgs, setPreviewMsgs] = useState([]);
  const [previewMenuOpen, setPreviewMenuOpen] = useState(false);
  const [liveModalOpen, setLiveModalOpen] = useState(false);
  const previewBodyRef = useRef(null);
  const previewReplyRef = useRef(null);
  const previewMenuRef = useRef(null);

  const [err, setErr] = useState('');
  const [emailExists, setEmailExists] = useState(false);
  const [loading, setLoading] = useState(false);

  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const handleLanguageChange = (lang) => { i18n.changeLanguage(lang); localStorage.setItem('language', lang); };

  // ── scroll lock + ResizeObserver-loop guard (the embedded chat preview re-measures
  //    on mount, which otherwise trips CRA's dev overlay) ──
  useEffect(() => {
    const html = document.documentElement, body = document.body;
    const ph = html.style.overflow, pb = body.style.overflow;
    html.style.overflow = 'hidden'; body.style.overflow = 'hidden';
    const Native = window.ResizeObserver;
    if (Native && !Native.__wxoWrapped) {
      const Wrapped = class extends Native {
        constructor(cb) {
          super((entries, obs) => { window.requestAnimationFrame(() => { try { cb(entries, obs); } catch (_) { /* noop */ } }); });
        }
      };
      Wrapped.__wxoWrapped = true;
      window.ResizeObserver = Wrapped;
    }
    return () => {
      html.style.overflow = ph; body.style.overflow = pb;
      if (Native) window.ResizeObserver = Native;
    };
  }, []);

  useEffect(() => {
    if (!langOpen) return;
    const onDown = (e) => { if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [langOpen]);

  const applyPrefill = useCallback(({ email, site_url, site_id, first_name, wordpress_secret_id }) => {
    if (email) setUserEmail(email);
    if (site_url) { setSiteUrl(site_url); setUrl((u) => u || site_url); }
    if (site_id) setSiteId(site_id);
    if (first_name) setFirstName(first_name);
    if (wordpress_secret_id) setWordpressSecretId(Number(wordpress_secret_id));
  }, []);

  // ── entry: plugin >= 1.1.0 lands with ?connect=<one-time code>; older plugins pass
  //    the fields directly. The exchange is single-use — on any failure (expired,
  //    replayed, backend down) the wizard simply starts unprefilled, nothing is blocked. ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connectCode = params.get('connect');

    applyPrefill({
      email: params.get('email'),
      site_url: params.get('site_url'),
      site_id: params.get('site_id'),
      first_name: params.get('first_name'),
      wordpress_secret_id: params.get('wordpress_secret_id'),
    });

    // An already-authenticated owner (e.g. a refresh mid-flow) keeps their session.
    const tok = localStorage.getItem('token');
    const u = localStorage.getItem('user_id');
    if (tok) { setToken(tok); setStep((s) => Math.max(s, 1)); }
    if (u) setUid(Number(u));

    if (!connectCode) return;
    (async () => {
      try {
        const r = await fetch(`${API}auth/wordpress/exchange_connect_code`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: connectCode }),
        });
        if (!r.ok) return;
        applyPrefill(await r.json());
      } catch (e) {
        console.error('Connect code exchange failed:', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── resume: once we know both the owner and the site, ask the DB which step this
  //    site is on. Server-side only — never browser storage — so it survives device
  //    changes and cleared storage, exactly like the Wix flow. ──
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current || !token || !wordpressSecretId) return;
    resumedRef.current = true;
    (async () => {
      try {
        const r = await authFetch(`${API}wordpress/onboarding/state?wordpress_secret_id=${encodeURIComponent(wordpressSecretId)}`);
        const j = await r.json();
        if (j && j.bot_id) { setBot(j.bot_id); setStep((s) => Math.max(Number(j.step) || 2, 2, s)); }
      } catch (e) { /* best-effort — the wizard just starts at the build step */ }
    })();
  }, [token, wordpressSecretId]);

  // Step 4: default the first notification-email slot to the account email, keeping it
  // synced until the user edits the slot themselves.
  useEffect(() => {
    if (!userEmail || notifyEmailUserEditedRef.current) return;
    setNotificationEmails((prev) => { const next = prev.length ? prev.slice() : ['']; next[0] = userEmail; return next; });
  }, [userEmail]);

  // Step 5: stop the plan poll on unmount and whenever we leave the pricing step.
  useEffect(() => () => { if (planPollRef.current) clearInterval(planPollRef.current); }, []);
  useEffect(() => {
    if (step !== 5 && planPollRef.current) { clearInterval(planPollRef.current); planPollRef.current = null; setIsCheckingPlan(false); }
  }, [step]);

  // Step 5: load the subscription plans (cards are entirely DB-driven).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}subscription_plans`);
        const data = await r.json();
        if (alive && Array.isArray(data)) setPlans(data);
      } catch (e) { /* best-effort */ }
      finally { if (alive) setPlansLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const monthlyByName = useMemo(() => { const m = {}; plans.forEach((p) => { if (p.billing_interval !== 'year') m[p.name] = p; }); return m; }, [plans]);
  const yearlyByName = useMemo(() => { const m = {}; plans.forEach((p) => { if (p.billing_interval === 'year') m[p.name] = p; }); return m; }, [plans]);
  const pricingTiers = useMemo(() => WPO_TIER_ORDER.map((n) => monthlyByName[n]).filter(Boolean), [monthlyByName]);
  const pricingSavings = useCallback((name) => {
    const m = monthlyByName[name]; const y = yearlyByName[name];
    const mp = stripeDisplayPrice(m); const yp = stripeDisplayPrice(y);
    if (m && y && mp > 0) return { pct: Math.round(((mp - yp) / mp) * 100), yearlyMonthly: yp, monthly: mp };
    return null;
  }, [monthlyByName, yearlyByName]);

  // ── business context: generated ONCE, from the real ingested content, after every
  //    source we're waiting on has finished. ──
  const generateBusinessContext = useCallback(async (botId) => {
    if (!botId) return;
    try {
      await authFetch(`${API}onboarding/generate_business_context`, {
        method: 'POST', body: JSON.stringify({ bot_id: botId }),
      });
    } catch (e) { console.error('business context generation failed', e); }
  }, []);

  const maybeGenerateBusinessContext = useCallback((botId) => {
    if (bizContextFiredRef.current) return;
    const expected = srcExpectedRef.current;
    if (expected.size === 0) return;
    for (const src of expected) { if (!srcDoneRef.current.has(src)) return; }
    bizContextFiredRef.current = true;
    generateBusinessContext(botId);
  }, [generateBusinessContext]);

  const markSourceDone = useCallback((name, botId) => {
    srcDoneRef.current.add(name);
    maybeGenerateBusinessContext(botId);
  }, [maybeGenerateBusinessContext]);

  // ── bind the agent to the plugin at the EARLIEST possible point (both ids known),
  //    not at the end of the wizard: an owner who abandons at Design or Pricing must
  //    still end up with a live agent on their site. Idempotent on the backend and
  //    ref-guarded here. A push:'failed' is SUCCESS — plugin >= 1.1.0 pulls the bot
  //    via my_bot within a minute; we only tell the owner about the short delay. ──
  const ensureWidgetActivated = useCallback(async () => {
    if (activatedRef.current || !bot || !siteId || !siteUrl) return;
    activatedRef.current = true;
    try {
      const r = await authFetch(`${API}wordpress/activate_chatbot`, {
        method: 'POST',
        body: JSON.stringify({ site_id: siteId, site_url: siteUrl, bot_id: bot }),
      });
      if (!r.ok) throw new Error(`activate_chatbot returned ${r.status}`);
      const j = await r.json();
      setPushFailed(j.push === 'failed');
      saveInteraction({ action: `wordpress_widget_activated_email_${userEmail}` });
    } catch (e) {
      // Never block onboarding on this. Drop the guard so a later step retries.
      activatedRef.current = false;
      console.error('WordPress widget activation failed:', e);
    }
  }, [bot, siteId, siteUrl, userEmail]);

  useEffect(() => { ensureWidgetActivated(); }, [ensureWidgetActivated]);

  // ── single WebSocket: website-training + document-ingest progress ──
  useEffect(() => {
    if (!uid || !bot) return;
    const raw = process.env.REACT_APP_API_WEBSOCKET_URL;
    let ws;
    try {
      if (!raw) throw new Error('WebSocket URL missing');
      ws = new WebSocket(raw.replace(/^http/, 'ws'));
    } catch (e) { console.error('WS init failed', e); return; }

    ws.addEventListener('open', () => {
      ws.send(JSON.stringify({ type: 'init', user_id: uid, token: token || localStorage.getItem('token') }));
    });

    ws.addEventListener('message', async (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }

      // (a) website discovery finished -> kick off scraping
      if (msg.type === 'find_new_links_result' && msg.request_id === trainingReqRef.current) {
        const links = (msg.new_found_links || []).slice(0, 50); // first scrape: cap at 50 pages
        if (!links.length) {
          setTraining({ active: true, phase: 'none', found: 0, done: 0 });
          setTimeout(() => setTraining((tr) => ({ ...tr, active: false })), 2600);
          markSourceDone('website', bot);
          return;
        }
        setTraining({ active: true, phase: 'training', found: links.length, done: 0 });
        try {
          const r = await authFetch(`${API}scrape_link`, {
            method: 'POST',
            body: JSON.stringify({ bot_id: bot, user_id: uid, urls: links }),
          });
          const j = await r.json();
          (j.knowledge_ids || []).forEach((id) => trainingKnowledgeRef.current.add(id));
          if (!trainingKnowledgeRef.current.size) markSourceDone('website', bot);
        } catch (e) { console.error('scrape_link failed', e); markSourceDone('website', bot); }
        return;
      }
      if (msg.type === 'find_new_links_error' && msg.request_id === trainingReqRef.current) {
        setTraining({ active: true, phase: 'none', found: 0, done: 0 });
        setTimeout(() => setTraining((tr) => ({ ...tr, active: false })), 2600);
        markSourceDone('website', bot);
        return;
      }

      // (b) per-page scrape status -> advance the bar
      if (msg.knowledge_id != null && trainingKnowledgeRef.current.has(msg.knowledge_id)) {
        if (msg.status === 'completed' || msg.status === 'failed') {
          setTraining((tr) => {
            const done = Math.min(tr.found, tr.done + 1);
            if (done >= tr.found && tr.found > 0) {
              setTimeout(() => setTraining((x) => ({ ...x, active: false })), DONE_HIDE_MS);
              markSourceDone('website', bot);
              return { ...tr, done, phase: 'done' };
            }
            return { ...tr, done };
          });
        }
        return;
      }

      // (c) per-document ingest status -> advance the documents bar
      if (msg.knowledge_id != null && docKnowledgeRef.current.has(msg.knowledge_id)) {
        if (msg.status === 'completed' || msg.status === 'failed') {
          setDocSync((d) => {
            const done = Math.min(d.total, d.done + 1);
            if (done >= d.total && d.total > 0) {
              setTimeout(() => setDocSync((x) => ({ ...x, active: false })), DONE_HIDE_MS);
              markSourceDone('docs', bot);
              return { ...d, done, phase: 'done' };
            }
            return { ...d, done };
          });
        }
        return;
      }
    });

    return () => ws.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, bot]);

  // ── persist progress to the DB only (single source of truth). Only the resumable
  //    steps (>=2, agent built) are saved; the build step is recovered from the site's
  //    bound bot by /wordpress/onboarding/state. ──
  useEffect(() => {
    if (step < 2 || !bot || !uid || !token) return;
    authFetch(`${API}onboarding/progress`, { method: 'PUT', body: JSON.stringify({ step, bot_id: bot }) }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, bot, uid, token]);

  // ── fetch the widget config when entering the Design step ──
  //
  // The agent is created INSTANTLY and its widget row is written by the BACKGROUND
  // finisher, so a moment after the build step it may not exist yet. The Wix flow
  // never hits this because its scan + skill-picker reveal sits in between; here the
  // build goes straight to Design, so we poll for it. Uses the RAW fetch on purpose:
  // the global interceptor turns every 404 into an error modal, and these 404s are
  // expected, not failures.
  useEffect(() => {
    if (step !== 2 || !bot || widgetFetchedRef.current) return;
    widgetFetchedRef.current = true;
    let cancelled = false;
    (async () => {
      const rawFetch = window._originalFetch || fetch;
      let d = null;
      for (let i = 0; i < 15 && !cancelled; i++) {
        try {
          const res = await rawFetch(`${API}widget_configuration/${encodeURIComponent(bot)}`);
          if (res.ok) { d = await res.json(); break; }
        } catch (e) { /* keep waiting */ }
        await new Promise((r) => setTimeout(r, 1000));
      }
      // Never block the design step on this — DEFAULT_WIDGET is perfectly usable and
      // the save is a PUT, so the owner's choices land either way.
      if (cancelled || !d) return;
      setWidget((w) => ({
        ...w,
        themeColor: d.theme_color || w.themeColor,
        headerColor: d.header_font_color || w.headerColor,
        hoverColor: d.button_hover_color || w.hoverColor,
        pulsing: d.pulsing ?? w.pulsing,
        widgetHorizontalAlignment: d.widget_horizontal_alignment || w.widgetHorizontalAlignment,
        widgetBorderRadius: d.widget_border_radius ?? w.widgetBorderRadius,
        widgetSize: d.widget_size ?? w.widgetSize,
        headerText: d.header_text || w.headerText,
        welcomeMessage: Array.isArray(d.welcome_message) ? (d.welcome_message[0] || w.welcomeMessage) : (d.welcome_message || w.welcomeMessage),
        icon: d.widget_icon_path || '',
        logo: d.header_icon_path || '',
        avatar: d.avatar_icon_path || '',
      }));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, bot]);

  // ── design preview housekeeping ──
  useEffect(() => {
    const el = previewBodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [previewMsgs]);
  useEffect(() => () => { if (previewReplyRef.current) clearTimeout(previewReplyRef.current); }, []);
  useEffect(() => {
    if (!previewMenuOpen) return undefined;
    const onDown = (e) => { if (previewMenuRef.current && !previewMenuRef.current.contains(e.target)) setPreviewMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [previewMenuOpen]);

  // ── auth handlers ──
  async function handleRegister(e) {
    e.preventDefault();
    setErr(''); setEmailExists(false);
    const email = (userEmail || '').trim(), first = (firstName || '').trim();
    if (!EMAIL_RE.test(email)) { setErr(t('wxo_err_email')); return; }
    if (!first) { setErr(t('wxo_err_first_name')); return; }
    if (pw.length < 8) { setErr(t('wxo_err_password_short')); return; }
    if (pw !== pw2) { setErr(t('wxo_err_password_match')); return; }
    try {
      setLoading(true);
      const r = await fetch(`${API}register/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password: pw, given_name: first, send_email: true, language: currentLanguage, signup_context: 'wordpress' }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        if (j.detail === 'Email already registered') setEmailExists(true);
        else setErr(t('wxo_err_create_account'));
        return;
      }
      const j = await r.json();
      setUid(j.user_id); setToken(j.token);
      if (j.token) localStorage.setItem('token', j.token);
      if (j.user_id) localStorage.setItem('user_id', String(j.user_id));
      saveInteraction({ action: `wordpress_account_created_email_${email}` });
      setStep((s) => Math.max(s, 1));
    } catch { setErr(t('wxo_err_create_account')); }
    finally { setLoading(false); }
  }

  async function handleLogin(e) {
    e.preventDefault();
    setErr('');
    try {
      setLoading(true);
      const r = await fetch(`${API}login/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail, password: pw }),
      });
      if (r.ok) {
        const j = await r.json();
        setUid(j.user_id); setToken(j.token);
        if (j.token) localStorage.setItem('token', j.token);
        if (j.user_id) localStorage.setItem('user_id', String(j.user_id));
        if (j.first_name) setFirstName(j.first_name);
        setStep((s) => Math.max(s, 1));
      } else {
        const j = await r.json().catch(() => ({}));
        setErr(j.detail || t('wxo_err_invalid_login'));
      }
    } catch { setErr(t('wxo_err_generic')); }
    finally { setLoading(false); }
  }

  // ── connect: instant bot + (optional) background website training ──
  function domainName(input) {
    try {
      const u = new URL(input.startsWith('http') ? input : 'https://' + input);
      const host = u.hostname.replace(/^www\./, '');
      const parts = host.split('.');
      return parts.length >= 2 ? parts[parts.length - 2] : host;
    } catch { return 'agent'; }
  }

  // Accepts anything that looks like a host: any TLD length (".photography" is as real
  // as ".com"), an optional scheme, an optional port, and an optional path. The old
  // {2,6} cap silently rejected real customers on .website / .digital / .services.
  function isValidUrlSimple(value) {
    if (!value) return false;
    const trimmed = value.trim();
    const regex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d{2,5})?([/?#].*)?$/;
    return regex.test(trimmed);
  }

  async function startWebsiteTraining(botId, trainUrl) {
    // Belt and braces beside the `existing` check in createBotAndContinue: ONE website
    // run per agent per session, whatever route got us here. A second run is not
    // harmless — find_new_links only returns pages we don't have yet, so it would crawl
    // the next 50 instead of doing nothing.
    if (trainedBotsRef.current.has(botId)) return;
    trainedBotsRef.current.add(botId);
    srcExpectedRef.current.add('website');
    setTraining({ active: true, phase: 'searching', found: 0, done: 0 });
    trainingKnowledgeRef.current = new Set();
    const reqId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    trainingReqRef.current = reqId;
    try {
      await authFetch(`${API}find_new_links`, { method: 'POST', body: JSON.stringify({ bot_id: botId, url: trainUrl, request_id: reqId }) });
    } catch (e) { console.error('find_new_links failed', e); setTraining((tr) => ({ ...tr, active: false })); markSourceDone('website', botId); }
  }

  async function startDocumentIngest(botId, urls) {
    srcExpectedRef.current.add('docs');
    setDocSync({ active: true, phase: 'processing', done: 0, total: urls.length });
    docKnowledgeRef.current = new Set();
    try {
      const r = await authFetch(`${API}ingest_document_urls`, {
        method: 'POST', body: JSON.stringify({ bot_id: botId, user_id: uid, document_urls: urls }),
      });
      const j = await r.json();
      (j.knowledge_ids || []).forEach((id) => docKnowledgeRef.current.add(id));
      if (!docKnowledgeRef.current.size) { setDocSync({ active: false, phase: 'idle', done: 0, total: 0 }); markSourceDone('docs', botId); }
    } catch (e) {
      console.error('ingest_document_urls failed', e);
      setDocSync({ active: false, phase: 'idle', done: 0, total: 0 });
      markSourceDone('docs', botId);
    }
  }

  function handleDocsAdded(files) {
    setDocFiles((prev) => [...prev, ...(files || [])]);
    setDocModalOpen(false);
  }
  function removeDoc(i) { setDocFiles((prev) => prev.filter((_, idx) => idx !== i)); }

  // WordPress has no sub-app detection, so the build goes straight on to Design; the
  // website keeps learning in the background with a docked progress strip.
  async function createBotAndContinue() {
    setErr('');
    const site = url.trim();
    if (site && !isValidUrlSimple(site)) { setErr(t('onboard_invalid_site_url_wp')); return; }
    try {
      setLoading(true);
      const r = await authFetch(`${API}wordpress/onboarding/create_bot`, {
        method: 'POST',
        body: JSON.stringify({
          wordpress_secret_id: wordpressSecretId,
          bot_name: site ? domainName(site) : 'My agent',
          url: site || null,
        }),
      });
      // 409 = this site already has an agent in a DIFFERENT account. Generic "build
      // failed" copy would leave the owner stuck with no idea what to do, so name the
      // cause and point at the login switch.
      if (r.status === 409) {
        setErr(
          <Trans i18nKey="wpo_err_site_other_account" components={[
            <button type="button" key="login" className="wxo-err-link" onClick={() => { setErr(''); setPw(''); setPw2(''); setAuthMode('login'); setStep(0); }} />,
          ]} />
        );
        return;
      }
      const j = await r.json();
      if (!j.bot_id) throw new Error(j.error || 'no bot id');
      const botId = j.bot_id;

      // The site ALREADY had an agent and the backend handed it back instead of
      // rebinding (reachable by going Back to the account step, logging in again and
      // pressing Build a second time). Its website is already learned, so starting
      // another run would NOT be a no-op: find_new_links returns only pages we don't
      // have yet, so a second build silently crawls the NEXT 50 pages, a third the 50
      // after that. Resume at Design instead. Documents the owner just picked are
      // genuinely new, so those still get ingested.
      if (j.existing) {
        setBot(botId);
        if (docFiles.length) startDocumentIngest(botId, docFiles.map((f) => f.url));
        setStep(2);
        return;
      }

      bizContextFiredRef.current = false;
      srcExpectedRef.current = new Set();
      srcDoneRef.current = new Set();
      setBot(botId);
      if (window.dataLayer) window.dataLayer.push({ event: 'bot_creation_started' });
      saveInteraction({ action: `wordpress_agent_created_email_${userEmail}` });
      // Ingest jobs run IN PARALLEL in the background; we never block the wizard on them.
      if (site) startWebsiteTraining(botId, site);
      if (docFiles.length) startDocumentIngest(botId, docFiles.map((f) => f.url));
      setStep(2);
    } catch (e) {
      console.error(e);
      setErr(t('wxo_err_build'));
    } finally { setLoading(false); }
  }

  // ── design ──
  const setStyle = (k, v) => setWidget((w) => ({ ...w, [k]: v }));
  function handleImageUpload(e, key) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setStyle(key, ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  }
  async function uploadImage(base64) {
    const fd = new FormData();
    fd.append('file', base64ToFile(base64, `icon-${Date.now()}.png`));
    const res = await fetch(`${API}upload_image/`, { method: 'POST', body: fd });
    if (!res.ok) throw new Error('upload failed');
    const j = await res.json();
    return j.file_path;
  }
  async function saveDesignAndContinue() {
    setErr('');
    try {
      setLoading(true);
      let iconPath = widget.icon;
      if (iconPath && iconPath.startsWith('data:image/')) iconPath = await uploadImage(iconPath);
      let logoPath = widget.logo;
      if (logoPath && logoPath.startsWith('data:image/')) logoPath = await uploadImage(logoPath);
      let avatarPath = widget.avatar;
      if (avatarPath && avatarPath.startsWith('data:image/')) avatarPath = await uploadImage(avatarPath);
      const updates = {
        theme_color: widget.themeColor,
        header_font_color: widget.headerColor,
        button_hover_color: widget.hoverColor,
        pulsing: widget.pulsing,
        widget_horizontal_alignment: widget.widgetHorizontalAlignment,
        widget_size: widget.widgetSize,
        widget_border_radius: widget.widgetBorderRadius,
        header_text: widget.headerText,
        welcome_message: [widget.welcomeMessage],
        widget_icon_path: iconPath || '',
        header_icon_path: logoPath || '',
        avatar_icon_path: avatarPath || '',
      };
      const res = await fetch(`${API}widget_configuration/${encodeURIComponent(bot)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error('save failed');
      if (iconPath !== widget.icon) setStyle('icon', iconPath || '');
      if (logoPath !== widget.logo) setStyle('logo', logoPath || '');
      if (avatarPath !== widget.avatar) setStyle('avatar', avatarPath || '');
      setStep(3);
    } catch (e) { console.error(e); setErr(t('wxo_design_save_error')); }
    finally { setLoading(false); }
  }

  const imageUploadControl = (key, labelKey, ref) => (
    <div className="wxo-control-group">
      <div className="wxo-color-picker-container">
        <label className="wxo-widget-setting-title">{t(labelKey)}</label>
        <div className="wxo-logo-upload">
          {widget[key] && <img className="wxo-logo-preview" src={widget[key]} alt="" />}
          <button type="button" className="wxo-logo-upload-btn" onClick={() => ref.current && ref.current.click()}>
            <FontAwesomeIcon icon={faUpload} /><span>{widget[key] ? t('wxo_design_replace') : t('wxo_design_upload')}</span>
          </button>
          {widget[key] && <button type="button" className="wxo-logo-remove-btn" onClick={() => setStyle(key, '')} aria-label="remove"><FontAwesomeIcon icon={faXmark} /></button>}
          <input ref={ref} type="file" accept="image/*" onChange={(e) => handleImageUpload(e, key)} style={{ display: 'none' }} />
        </div>
      </div>
    </div>
  );

  function sendPreview() {
    const text = previewInput.trim();
    if (!text) return;
    setPreviewMsgs((m) => [...m, { role: 'user', text }]);
    setPreviewInput('');
    if (previewReplyRef.current) clearTimeout(previewReplyRef.current);
    previewReplyRef.current = setTimeout(() => {
      setPreviewMsgs((m) => [...m, { role: 'bot', text: t('wxo_design_preview_reply') }]);
    }, 500);
  }
  function clearPreviewChat() {
    if (previewReplyRef.current) clearTimeout(previewReplyRef.current);
    setPreviewMsgs([]);
    setPreviewInput('');
    setPreviewMenuOpen(false);
  }
  function talkToHumanPreview() {
    setPreviewMenuOpen(false);
    setLiveModalOpen(true);
  }

  // ── step 4 — validation + save to notification_settings ──
  const notifyEmailRequired = WPO_NOTIFY_EVENTS.some((ev) => !wpoChannelLocked(ev.key, 'email') && Boolean(notifyPrefs[ev.key]?.email));
  const notifyCleanEmails = (notificationEmails || []).map((e) => String(e).trim()).filter((e) => e.length);
  const notifyInvalidEmails = notifyCleanEmails.filter((e) => !EMAIL_RE.test(e));
  const notifyEmailMissing = notifyEmailRequired && notifyCleanEmails.length === 0;
  const notifyEmailHasError = notifyEmailMissing || notifyInvalidEmails.length > 0;

  async function saveNotificationsAndContinue() {
    setNotifySaveAttempted(true);
    if (notifyEmailHasError) return;
    setErr('');
    setLoading(true);
    try {
      const valid = notifyCleanEmails.filter((e) => EMAIL_RE.test(e));
      const needs = (sec) => WPO_NOTIFY_EVENTS.some((ev) => ev.section === sec && !wpoChannelLocked(ev.key, 'email') && Boolean(notifyPrefs[ev.key]?.email));
      const payload = {
        prefs: notifyPrefs,
        emails_by_section: { live_chat: needs('live_chat') ? valid : [], leads: needs('leads') ? valid : [] },
        slack_webhooks_by_section: { live_chat: [], leads: [] },
        teams_webhooks_by_section: { live_chat: [], leads: [] },
        telegram_chat_ids_by_section: { live_chat: [], leads: [] },
      };
      if (bot) {
        try { await authFetch(`${API}notification_settings/${encodeURIComponent(bot)}`, { method: 'PUT', body: JSON.stringify(payload) }); }
        catch (e) { console.error('notification_settings PUT failed', e); }
      }
      setStep(5);
    } catch (e) { console.error(e); setStep(5); }
    finally { setLoading(false); }
  }

  // ── step 5 — Stripe checkout in a NEW TAB, then poll the bot's plan until it flips
  //    off "Free" (the user can't message us back from the Stripe-hosted page). ──
  function stopPlanPolling() {
    if (planPollRef.current) { clearInterval(planPollRef.current); planPollRef.current = null; }
    setIsCheckingPlan(false);
  }
  async function fetchBotPlanName() {
    if (!bot) throw new Error('no bot');
    const r = await fetch(`${API}bot?bot_id=${encodeURIComponent(bot)}&include_plan=true`);
    if (!r.ok) throw new Error('bot fetch failed');
    const j = await r.json();
    return j?.subscription_plan?.name ?? 'Free';
  }
  function startPlanPolling() {
    if (planPollRef.current || !bot) return;
    setPlanCheckError('');
    setIsCheckingPlan(true);
    planPollRef.current = setInterval(async () => {
      try {
        const name = await fetchBotPlanName();
        if (name && name !== 'Free') {
          if (window.dataLayer) window.dataLayer.push({ event: 'free_trial_started' });
          saveInteraction({ action: `wordpress_payment_confirmed_email_${userEmail}` });
          stopPlanPolling();
          setTrialStarted(true);
        }
      } catch (e) { setPlanCheckError(t('wxo_price_still_checking')); }
    }, 2000);
  }

  async function startTrialCheckout(planName) {
    const row = billingCycle === 'YEAR' ? (yearlyByName[planName] || monthlyByName[planName]) : monthlyByName[planName];
    if (!bot || !row?.stripe_subscription_plan_id) {
      console.error('pricing: missing bot or stripe price id, cannot open checkout');
      setErr(t('billing_checkout_start_error'));
      return;
    }
    setErr('');
    if (window.dataLayer) window.dataLayer.push({ event: 'checkout_session_started' });
    try {
      const res = await fetch(`${API}create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bot_id: bot,
          price_id: row.stripe_subscription_plan_id,
          subscription_plan_id: row.id,
          coupon_id: row.coupon_id,
          site_id: siteId || null,
        }),
      });
      const data = await res.json();
      if (!data?.checkout_url) throw new Error('no checkout url');
      saveInteraction({ action: `wordpress_checkout_session_started_email_${userEmail}` });
      // New tab, so this tab keeps the wizard state and can poll for activation.
      window.open(data.checkout_url, '_blank', 'noopener,noreferrer');
      startPlanPolling();
    } catch (e) {
      console.error('checkout failed:', e);
      setErr(t('billing_checkout_start_error'));
    }
  }

  async function goToPortal() {
    // Leaving the onboarding for the portal = onboarding finished.
    try { if (bot) await authFetch(`${API}onboarding/complete`, { method: 'POST', body: JSON.stringify({ bot_id: bot }) }); } catch (e) { /* best-effort */ }
    // Make sure the agent really is on the site even if an earlier attempt failed.
    await ensureWidgetActivated();
    if (onSetupComplete) onSetupComplete(token, uid);
    navigate('/');
  }

  // ── progress strips ──
  const doneTimer = () => (
    <span className="wxo-strip-timer" aria-hidden="true">
      <svg viewBox="0 0 24 24" className="wxo-strip-timer-svg">
        <circle className="wxo-strip-timer-track" cx="12" cy="12" r="10" />
        <circle className="wxo-strip-timer-ring" cx="12" cy="12" r="10" style={{ animationDuration: `${DONE_HIDE_MS}ms` }} />
      </svg>
      <FontAwesomeIcon icon={faCheck} className="wxo-strip-timer-check" />
    </span>
  );

  const trainStrip = training.active ? (
    <div className="wxo-train-strip">
      <div className="wxo-train-inner">
        {training.phase === 'searching' && (<><span className="wxo-train-spin" /><span className="wxo-train-label">{t('wxo_train_searching')}</span></>)}
        {training.phase === 'training' && (
          <div className="wxo-train-prog">
            <div className="wxo-train-row">
              <span className="wxo-train-label">{t('wxo_train_progress', { done: training.done, total: training.found })}</span>
              <span className="wxo-train-pct">{Math.round((training.done / Math.max(1, training.found)) * 100)}%</span>
            </div>
            <div className="wxo-train-bar"><div className="wxo-train-fill" style={{ width: `${Math.round((training.done / Math.max(1, training.found)) * 100)}%` }} /></div>
          </div>
        )}
        {training.phase === 'done' && (<>{doneTimer()}<span className="wxo-train-label">{t('wxo_train_done', { count: training.found })}</span></>)}
        {training.phase === 'none' && (<span className="wxo-train-label">{t('wxo_train_none')}</span>)}
      </div>
    </div>
  ) : null;

  const docPct = Math.round((docSync.done / Math.max(1, docSync.total)) * 100);
  const docStrip = docSync.active ? (
    <div className="wxo-train-strip">
      <div className="wxo-train-inner">
        {docSync.phase === 'processing' ? (
          <div className="wxo-train-prog">
            <div className="wxo-train-row">
              <span className="wxo-train-label">{t('wxo_docs_processing', { done: docSync.done, total: docSync.total })}</span>
              <span className="wxo-train-pct">{docPct}%</span>
            </div>
            <div className="wxo-train-bar"><div className="wxo-train-fill" style={{ width: `${docPct}%` }} /></div>
          </div>
        ) : (
          <>{doneTimer()}<span className="wxo-train-label">{t('wxo_docs_done', { count: docSync.total })}</span></>
        )}
      </div>
    </div>
  ) : null;

  const progressStrips = (training.active || docSync.active) ? (<>{trainStrip}{docStrip}</>) : null;

  // ── render helpers ──
  const showBack = step > 0;
  const backBtn = showBack ? (
    <button className="wxo-back-inline" type="button" aria-label={t('wxo_back')}
      onClick={() => setStep((s) => Math.max(0, s - 1))}>
      <FontAwesomeIcon icon={faArrowLeft} /><span>{t('wxo_back')}</span>
    </button>
  ) : null;
  const titleRow = (heading) => <div className="wxo-title-row">{backBtn}{heading}</div>;

  const langToggle = (
    <div className="wxo-lang" ref={langRef}>
      <button type="button" className="wxo-lang-trigger" onClick={() => setLangOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={langOpen}>
        <FontAwesomeIcon icon={faGlobe} className="wxo-lang-icon" />
        <span className="wxo-lang-current">{currentLangLabel}</span>
      </button>
      {langOpen && (
        <div className="wxo-lang-menu" role="listbox">
          {SUPPORTED_LANGUAGES.map((l) => {
            const sel = l.code === currentLanguage;
            return (
              <button key={l.code} type="button" role="option" aria-selected={sel}
                className={`wxo-lang-option ${sel ? 'wxo-sel' : ''}`}
                onClick={() => { handleLanguageChange(l.code); setLangOpen(false); }}>
                <span className="wxo-lang-check">{sel && <FontAwesomeIcon icon={faCheck} />}</span>{l.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  // Only one card is mounted at a time. Its width snaps per step (the fade-in masks
  // the change), and the anim key replays the entrance on each state.
  let stageW = 480;
  if (step === 0) stageW = 980;        // account: form + brand visual, two columns
  else if (step === 1) stageW = 540;   // connect: url + documents
  else if (step === 2) stageW = 920;   // design
  else if (step === 3) stageW = 920;   // test
  else if (step === 4) stageW = 760;   // notifications matrix
  else if (step === 5) stageW = 1000;  // pricing — three plan cards side by side
  const animKey = `s-${step}`;

  // The primary "continue" CTA renders BELOW the card on the purple shell. Account
  // (form submits) and Pricing (its own plan CTAs) keep their in-card buttons.
  let stageCta = null;
  if (step === 1) stageCta = { label: t('wxo_build_agent'), onClick: createBotAndContinue, busy: loading };
  else if (step === 2) stageCta = { label: t('wxo_to_test'), onClick: saveDesignAndContinue, busy: loading };
  else if (step === 3) stageCta = { label: t('wxo_test_continue'), onClick: () => setStep(4), busy: false };
  else if (step === 4) stageCta = { label: t('wxo_notif_continue'), onClick: saveNotificationsAndContinue, busy: loading };

  return (
    <div className="wxo-scope wxo-wrapper">
      <img className="wxo-logo" src="/ultimo-bots-white.png" alt="Ultimo Bots" />
      {langToggle}
      <div className="wxo-step-ind">
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <span key={i} className={`wxo-step-dot ${i === step ? 'wxo-current' : i < step ? 'wxo-done' : ''}`} />
        ))}
      </div>

      <div className={`wxo-stage ${stageCta ? 'wxo-has-cta' : ''}`} style={{ maxWidth: stageW }}>
        <div className="wxo-card-anim" key={animKey}>

          {/* ── Step 0 — Account ── */}
          {step === 0 && (
            <div className="wxo-card wxo-card-auth">
              <div className="wxo-auth-form">
                {authMode === 'register' ? (
                  <>
                    <h2 className="wxo-h2">{t('wxo_reg_title')}</h2>
                    <p className="wxo-sub">{t('wxo_reg_sub')}</p>
                    <form onSubmit={handleRegister} autoComplete="on">
                      <div className="wxo-field"><TextField label={t('wxo_first_name')} name="firstName" autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} fullWidth variant="outlined" required sx={FIELD_SX} /></div>
                      <div className="wxo-field"><TextField label={t('wxo_email')} name="email" type="email" autoComplete="email" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} fullWidth variant="outlined" required sx={FIELD_SX} /></div>
                      <div className="wxo-field"><TextField label={t('wxo_password')} name="new-password" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} fullWidth variant="outlined" required inputProps={{ minLength: 8 }} sx={FIELD_SX} /></div>
                      <div className="wxo-field"><TextField label={t('wxo_confirm_password')} name="confirm-password" type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} fullWidth variant="outlined" required inputProps={{ minLength: 8 }} sx={FIELD_SX} /></div>
                      {(emailExists || err) && (
                        <div className="wxo-err">
                          {emailExists
                            ? <Trans i18nKey="wxo_err_email_registered" components={[<button type="button" key="login" className="wxo-err-link" onClick={() => { setEmailExists(false); setErr(''); setPw(''); setPw2(''); setAuthMode('login'); }} />]} />
                            : err}
                        </div>
                      )}
                      <button className="wxo-btn wxo-btn-wide" type="submit" disabled={loading}>{t('wxo_get_started')} {loading ? <span className="wxo-spin" /> : <FontAwesomeIcon icon={faArrowRight} />}</button>
                    </form>
                    <p className="wxo-link">{t('wxo_have_account')}{' '}<button type="button" onClick={() => { setErr(''); setEmailExists(false); setAuthMode('login'); }}>{t('wxo_log_in')}</button></p>
                  </>
                ) : (
                  <>
                    <h2 className="wxo-h2">{t('wxo_login_title')}</h2>
                    <p className="wxo-sub">{t('wxo_login_sub')}</p>
                    <form onSubmit={handleLogin} autoComplete="on">
                      <div className="wxo-field"><TextField label={t('wxo_email')} name="email" type="email" autoComplete="email" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} fullWidth variant="outlined" required sx={FIELD_SX} /></div>
                      <div className="wxo-field"><TextField label={t('wxo_password')} name="password" type="password" autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} fullWidth variant="outlined" required sx={FIELD_SX} /></div>
                      <div className="wxo-forgot-row">
                        <a className="wxo-forgot-link" href="/forgot-password" target="_blank" rel="noopener noreferrer">{t('login_forgot_password')}</a>
                      </div>
                      {err && <div className="wxo-err">{err}</div>}
                      <button className="wxo-btn wxo-btn-wide" type="submit" disabled={loading}>{t('wxo_log_in')} {loading ? <span className="wxo-spin" /> : <FontAwesomeIcon icon={faArrowRight} />}</button>
                    </form>
                    <p className="wxo-link">{t('wxo_new_here')}{' '}<button type="button" onClick={() => { setErr(''); setEmailExists(false); setPw(''); setPw2(''); setAuthMode('register'); }}>{t('wxo_create_account')}</button></p>
                  </>
                )}
              </div>
              <div className="wxo-auth-visual">
                <WixAuthVisual
                  actions={GENERIC_ACTIONS}
                  tools={GENERIC_TOOLS}
                  eyebrowKey="wpo_wav_eyebrow"
                  titleKey="wav_title"
                  subKey="wpo_wav_sub"
                />
              </div>
            </div>
          )}

          {/* ── Step 1 — Connect (no sub-app reveal: WordPress has no platform apps) ── */}
          {step === 1 && (
            <div className="wxo-card">
              {titleRow(<h2 className="wxo-h2">{t('wxo_connect_title')}</h2>)}
              <p className="wxo-sub">{t('wpo_connect_sub')}</p>
              {siteUrl && (
                <div className="wxo-site-banner">
                  <span className="wxo-site-dot" />
                  <span className="wxo-site-banner-text">{t('wpo_site_connected_to')} <strong>{siteUrl.replace(/^https?:\/\//, '')}</strong></span>
                </div>
              )}
              <div className="wxo-sources">
                <div className="wxo-source">
                  <div className="wxo-source-top">
                    <span className="wxo-source-ic"><FontAwesomeIcon icon={faGlobe} /></span>
                    <span className="wxo-source-label">{t('wxo_src_website')}</span>
                  </div>
                  <TextField label={t('wxo_url_placeholder')} type="text" value={url} onChange={(e) => setUrl(e.target.value)} fullWidth variant="outlined" size="small" sx={FIELD_SX} />
                </div>
                <div className={`wxo-source wxo-source-docs ${docFiles.length ? 'wxo-has' : ''}`} role="button" tabIndex={0}
                  onClick={() => setDocModalOpen(true)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDocModalOpen(true); } }}>
                  <div className="wxo-source-top">
                    <span className="wxo-source-ic"><FontAwesomeIcon icon={faFileArrowUp} /></span>
                    <span className="wxo-source-label">{t('wxo_src_documents')}</span>
                    {docFiles.length > 0
                      ? <button type="button" className="wxo-source-clear" onClick={(e) => { e.stopPropagation(); setDocFiles([]); }}>{t('wxo_docs_clear')}</button>
                      : <FontAwesomeIcon icon={faArrowRight} className="wxo-source-arrow" />}
                  </div>
                  {docFiles.length > 0 ? (
                    <>
                      <ul className="wxo-doc-list">
                        {docFiles.map((f, i) => (
                          <li key={`${f.url}-${i}`} className="wxo-doc-item">
                            <FontAwesomeIcon icon={faFileLines} className="wxo-doc-ic" />
                            <span className="wxo-doc-name" title={f.name}>{f.name}</span>
                            <button type="button" className="wxo-doc-rm" onClick={(e) => { e.stopPropagation(); removeDoc(i); }} aria-label={t('wxo_docs_remove')}><FontAwesomeIcon icon={faXmark} /></button>
                          </li>
                        ))}
                      </ul>
                      <span className="wxo-doc-more"><FontAwesomeIcon icon={faPlus} /> {t('wxo_docs_more')}</span>
                    </>
                  ) : (
                    <span className="wxo-source-hint">{t('wxo_docs_hint')}</span>
                  )}
                </div>
              </div>
              {err && <div className="wxo-err">{err}</div>}
            </div>
          )}

          {/* ── Step 2 — Design ── */}
          {step === 2 && (
            <div className="wxo-card">
              {titleRow(<h2 className="wxo-h2">{t('wxo_design_title')}</h2>)}
              <p className="wxo-sub">{t('wxo_design_sub')}</p>
              <div className="wxo-preview-card">
                <div className="wxo-user-settings">
                  <div className="wxo-control-group">
                    <TextField label={t('wxo_design_header')} value={widget.headerText} onChange={(e) => setStyle('headerText', e.target.value)}
                      fullWidth variant="outlined" inputProps={{ maxLength: 60 }}
                      helperText={`${(widget.headerText || '').length}/60`} sx={FIELD_SX} />
                  </div>
                  <div className="wxo-control-group">
                    <TextField label={t('wxo_design_welcome')} value={widget.welcomeMessage} onChange={(e) => setStyle('welcomeMessage', e.target.value)}
                      fullWidth multiline minRows={2} maxRows={4} variant="outlined" inputProps={{ maxLength: 300 }}
                      helperText={`${(widget.welcomeMessage || '').length}/300`} sx={FIELD_SX} />
                  </div>
                  <div className="wxo-control-group">
                    <div className="wxo-color-picker-container">
                      <label className="wxo-widget-setting-title">{t('wxo_design_theme')}</label>
                      <input type="color" value={widget.themeColor} onChange={(e) => setStyle('themeColor', e.target.value)} />
                    </div>
                  </div>
                  <div className="wxo-control-group">
                    <div className="wxo-color-picker-container">
                      <label className="wxo-widget-setting-title">{t('wxo_design_header_color')}</label>
                      <input type="color" value={widget.headerColor} onChange={(e) => setStyle('headerColor', e.target.value)} />
                    </div>
                  </div>
                  {imageUploadControl('icon', 'wxo_design_icon', iconInputRef)}
                  {imageUploadControl('logo', 'wxo_design_logo', logoInputRef)}
                  {imageUploadControl('avatar', 'wxo_design_avatar', avatarInputRef)}
                </div>

                <div className="wxo-divider" />

                <div className="wxo-preview-area">
                  <div className="wxo-chat-bubble" style={{ background: widget.icon ? 'transparent' : widget.themeColor }}>
                    {widget.icon ? <img src={widget.icon} alt="" /> : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="58%" viewBox="0 0 24 24" fill="#fff"><path fillRule="evenodd" d="M12 2.25c-2.429 0-4.817.178-7.152.521C2.87 3.061 1.5 4.795 1.5 6.741v6.018c0 1.946 1.37 3.68 3.348 3.97.877.129 1.761.234 2.652.316V21a.75.75 0 0 0 1.28.53l4.184-4.183a.39.39 0 0 1 .266-.112c2.006-.05 3.982-.22 5.922-.506 1.978-.29 3.348-2.023 3.348-3.97V6.741c0-1.947-1.37-3.68-3.348-3.97A49.145 49.145 0 0 0 12 2.25ZM8.25 8.625a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Zm2.625 1.125a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875-1.125a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Z" clipRule="evenodd" /></svg>
                    )}
                  </div>
                  <div className="wxo-chat-window">
                    <div className="wxo-chat-header" style={{ backgroundColor: widget.themeColor }}>
                      <div className="wxo-chat-header-title">
                        <span className="wxo-chat-header-brand">
                          {widget.logo && <img className="wxo-chat-logo" src={widget.logo} alt="" />}
                          <span className="wxo-chat-title" style={{ color: widget.headerColor }}>{widget.headerText || 'Chat with us'}</span>
                        </span>
                        <span className="wxo-chat-actions" ref={previewMenuRef}>
                          <button type="button" className="wxo-chat-kebab" style={{ color: widget.headerColor }}
                            aria-label={t('wxo_design_menu_more')} aria-haspopup="menu" aria-expanded={previewMenuOpen}
                            onClick={() => setPreviewMenuOpen((o) => !o)}>
                            <FontAwesomeIcon icon={faEllipsis} />
                          </button>
                          <button type="button" className="wxo-chat-close" style={{ color: widget.headerColor }} aria-label="close"><FontAwesomeIcon icon={faXmark} /></button>
                          {previewMenuOpen && (
                            <div className="wxo-chat-menu" role="menu">
                              <button type="button" className="wxo-chat-menu-item" role="menuitem" onClick={clearPreviewChat}>
                                <FontAwesomeIcon icon={faTrash} /> {t('wxo_design_menu_clear')}
                              </button>
                              <button type="button" className="wxo-chat-menu-item" role="menuitem" onClick={talkToHumanPreview}>
                                <FontAwesomeIcon icon={faHeadset} /> {t('wxo_design_menu_human')}
                              </button>
                            </div>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="wxo-chat-body" ref={previewBodyRef}>
                      <div className="wxo-chat-row bot">
                        {widget.avatar && <img className="wxo-chat-avatar" src={widget.avatar} alt="" />}
                        <div className="wxo-chat-msg">{widget.welcomeMessage || 'Hi, how can I assist you?'}</div>
                      </div>
                      {previewMsgs.map((m, i) => (
                        <div key={i} className={`wxo-chat-row ${m.role}`}>
                          {m.role === 'bot' && widget.avatar && <img className="wxo-chat-avatar" src={widget.avatar} alt="" />}
                          <div className={`wxo-chat-msg ${m.role === 'user' ? 'wxo-chat-msg-user' : 'wxo-chat-msg-md'}`}
                            style={m.role === 'user' ? { background: widget.themeColor, borderColor: widget.themeColor } : undefined}>
                            {m.role === 'bot' ? <ReactMarkdown>{m.text}</ReactMarkdown> : m.text}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="wxo-chat-footer">
                      <div className="wxo-live-cta-row">
                        <button type="button" className="wxo-live-cta-btn" onClick={() => setLiveModalOpen(true)}>
                          <FontAwesomeIcon icon={faHeadset} /><span>{t('wxo_design_menu_human')}</span>
                        </button>
                      </div>
                      <div className="wxo-powered"><span className="wxo-powered-text">Powered by Ultimo Bots</span></div>
                      <div className="wxo-input-send">
                        <input className="wxo-chat-input" type="text" value={previewInput} placeholder={t('chat_input_placeholder')}
                          onChange={(e) => setPreviewInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendPreview(); } }} />
                        <button type="button" className="wxo-chat-send" style={{ backgroundColor: widget.themeColor, color: widget.headerColor }}
                          onClick={sendPreview} disabled={!previewInput.trim()}><FontAwesomeIcon icon={faArrowUp} /></button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {err && <div className="wxo-err">{err}</div>}
              {progressStrips}
            </div>
          )}

          {/* ── Step 3 — Test ── */}
          {step === 3 && (
            <div className="wxo-card wxo-card-test">
              {titleRow(<h2 className="wxo-h2">{t('wxo_test_title')}</h2>)}
              {/* NOT wxo_test_sub — that one promises bookings / products / menus,
                  which are Wix sub-app capabilities a WordPress agent does not have
                  out of the box. */}
              <p className="wxo-sub">{t('wpo_test_sub')}</p>
              {bot ? (
                <div className="wxo-test-chat">
                  <ChatModal
                    embedded
                    open
                    onClose={() => {}}
                    bot={{ id: bot, knowledge_count: 1 }}
                    currentMessageCount={0}
                    messagePlanLimit={999999}
                    planName="trial"
                    openSubscriptionModalFromChat={() => {}}
                    onMessageCountChange={() => {}}
                    liveCtaAlways
                    onLiveCta={() => setLiveModalOpen(true)}
                    // Onboarding = meeting the agent, not tuning it. The
                    // "Improve this answer" CTA stays a portal surface.
                    showImprove={false}
                  />
                </div>
              ) : (
                <div className="wxo-scan"><div className="wxo-scan-ring" /><span>{t('wxo_test_preparing')}</span></div>
              )}
              {progressStrips}
            </div>
          )}

          {/* ── Step 4 — Notifications ── */}
          {step === 4 && (
            <div className="wxo-card">
              {titleRow(<h2 className="wxo-h2">{t('wxo_notif_title')}</h2>)}
              <p className="wxo-sub">{t('wxo_notif_sub')}</p>

              <div className="wxo-notify-matrix">
                <div className="wxo-notify-row wxo-notify-head">
                  <div className="wxo-notify-event-col" />
                  {WPO_NOTIFY_CHANNELS.map((ch) => (
                    <div key={ch.key} className="wxo-notify-channel-col">
                      <FontAwesomeIcon icon={ch.icon} className="wxo-notify-channel-icon" />
                      <span className="wxo-notify-channel-label">{t(ch.labelKey)}</span>
                    </div>
                  ))}
                </div>
                {WPO_NOTIFY_EVENTS.map((ev) => {
                  const row = notifyPrefs[ev.key] || {};
                  return (
                    <div key={ev.key} className={`wxo-notify-row ${ev.highlight ? 'wxo-notify-row-hi' : ''}`}>
                      <div className="wxo-notify-event-col">
                        <div className="wxo-notify-event-title"><FontAwesomeIcon icon={ev.icon} className="wxo-notify-event-icon" />{t(ev.titleKey)}</div>
                        <div className="wxo-notify-event-desc">{t(ev.descKey)}</div>
                      </div>
                      {WPO_NOTIFY_CHANNELS.map((ch) => {
                        const locked = wpoChannelLocked(ev.key, ch.key);
                        const checkbox = (
                          <Checkbox
                            checked={locked ? false : Boolean(row[ch.key])}
                            disabled={locked}
                            onChange={() => {
                              setNotifySaveAttempted(false);
                              setNotifyPrefs((prev) => ({ ...prev, [ev.key]: { ...(prev[ev.key] || {}), [ch.key]: !prev[ev.key]?.[ch.key] } }));
                            }}
                            sx={{ '&.Mui-checked': { color: '#5616ea' }, '&:hover': { backgroundColor: '#5616ea15' }, '&.Mui-disabled': { color: '#e0e0e6' }, padding: '4px' }}
                          />
                        );
                        return (
                          <div key={ch.key} className="wxo-notify-channel-col">
                            {locked ? (
                              <Tooltip title={t('wxo_notif_locked')} arrow><span style={{ cursor: 'not-allowed', display: 'inline-flex' }}><span style={{ pointerEvents: 'none' }}>{checkbox}</span></span></Tooltip>
                            ) : checkbox}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              <div className="wxo-notify-portal">
                <span className="wxo-notify-portal-label">{t('wxo_notif_also')}</span>
                <span className="wxo-notify-chips">
                  <span className="wxo-notify-chip"><TelegramBrandIcon size={15} />Telegram</span>
                  <span className="wxo-notify-chip"><SlackBrandIcon size={15} />Slack</span>
                  <span className="wxo-notify-chip"><TeamsBrandIcon size={15} />Teams<span className="wxo-notify-soon">{t('wxo_notif_soon')}</span></span>
                </span>
              </div>

              {notifyEmailRequired && (
                <div className="wxo-notify-recipients">
                  <div className="wxo-notify-recipients-head"><FontAwesomeIcon icon={faEnvelope} style={{ color: '#5616ea' }} /><span>{t('wxo_notif_recipients')}</span></div>
                  <div className="wxo-notify-emails">
                    {(notificationEmails.length ? notificationEmails : ['']).map((value, idx) => {
                      const list = notificationEmails.length ? notificationEmails : [''];
                      const trimmed = String(value).trim();
                      const showError = notifySaveAttempted && trimmed.length > 0 && !EMAIL_RE.test(trimmed);
                      return (
                        <div key={idx} className="wxo-notify-email-row">
                          <TextField
                            label={t('wxo_notif_email_n', { n: idx + 1 })}
                            value={value}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (idx === 0) notifyEmailUserEditedRef.current = true;
                              setNotifySaveAttempted(false);
                              setNotificationEmails((prev) => { const next = prev.slice(); next[idx] = v; return next; });
                            }}
                            type="text" fullWidth variant="outlined" size="small" error={showError} placeholder="you@example.com"
                            InputProps={{ endAdornment: list.length > 1 ? (<InputAdornment position="end"><IconButton aria-label="Remove" edge="end" size="small" onClick={() => { setNotifySaveAttempted(false); setNotificationEmails((prev) => { const next = prev.filter((_, i) => i !== idx); return next.length ? next : ['']; }); }}><FontAwesomeIcon icon={faTrash} style={{ fontSize: 13 }} /></IconButton></InputAdornment>) : null }}
                            sx={FIELD_SX}
                          />
                        </div>
                      );
                    })}
                  </div>
                  {notificationEmails.length < 3 && (
                    <button type="button" className="wxo-notify-add-email" onClick={() => { setNotifySaveAttempted(false); setNotificationEmails((prev) => (prev.length >= 3 ? prev : [...prev, ''])); }} title={t('wxo_notif_add_email')}>
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  )}
                  {notifySaveAttempted && notifyEmailHasError && (
                    <div className="wxo-notify-error"><FontAwesomeIcon icon={faTriangleExclamation} /><span>{notifyEmailMissing ? t('wxo_notif_err_missing') : t('wxo_notif_err_invalid')}</span></div>
                  )}
                </div>
              )}

              {err && <div className="wxo-err">{err}</div>}
            </div>
          )}

          {/* ── Step 5 — pricing / start free trial (Stripe checkout, styled like /billing) ── */}
          {step === 5 && (
            <div className="wxo-card wxo-pricing">
              {trialStarted ? (
                <div className="wxo-trial-done">
                  <div className="wxo-trial-done-ic"><FontAwesomeIcon icon={faCheck} /></div>
                  <h2 className="wxo-h2">{t('wxo_price_done_title')}</h2>
                  <p className="wxo-sub">{t('wxo_price_done_sub')}</p>
                  {pushFailed && <p className="wxo-sub">{t('wpo_push_delayed')}</p>}
                  <button className="wxo-btn" onClick={goToPortal}>{t('wxo_price_to_portal')} <FontAwesomeIcon icon={faArrowRight} /></button>
                </div>
              ) : isCheckingPlan ? (
                <div className="wxo-trial-wait">
                  <div className="wxo-scan-ring" />
                  <h2 className="wxo-h2">{t('wxo_price_detecting_title')}</h2>
                  <p className="wxo-sub">{t('wxo_price_detecting_sub')}</p>
                  {planCheckError && <div className="wxo-err">{planCheckError}</div>}
                  <div className="wxo-trial-wait-actions">
                    <button className="wxo-btn-ghost" onClick={stopPlanPolling}>{t('wxo_price_back')}</button>
                    <button className="wxo-btn" onClick={async () => { try { const n = await fetchBotPlanName(); if (n !== 'Free') { stopPlanPolling(); setTrialStarted(true); } else { setPlanCheckError(t('wxo_price_still_checking')); } } catch { setPlanCheckError(t('wxo_price_still_checking')); } }}>{t('wxo_price_refresh')}</button>
                  </div>
                </div>
              ) : (
                <>
                  {titleRow(<h2 className="wxo-h2">{t('wxo_price_title')}</h2>)}

                  <div className="rk-billing-toggle">
                    <span className={`rk-billing-thumb${billingCycle === 'YEAR' ? ' right' : ''}`} aria-hidden />
                    <button type="button" className={billingCycle === 'MONTH' ? 'active' : ''} onClick={() => setBillingCycle('MONTH')}>{t('monthly')}</button>
                    <button type="button" className={billingCycle === 'YEAR' ? 'active' : ''} onClick={() => setBillingCycle('YEAR')}>{t('yearly')}</button>
                  </div>

                  {plansLoading && !pricingTiers.length ? (
                    <div className="wxo-trial-wait"><div className="wxo-scan-ring" /></div>
                  ) : (
                    <div className="rk-plans">
                      {pricingTiers.map((mRow) => {
                        const name = mRow.name;
                        const yRow = yearlyByName[name];
                        const row = billingCycle === 'YEAR' ? (yRow || mRow) : mRow;
                        const sv = pricingSavings(name);
                        const featured = name === WPO_FEATURED_TIER;
                        const rescrape = mRow.automatic_rescraping;
                        const removeBranding = !!mRow.remove_branding;
                        const canCheckout = Boolean(row?.stripe_subscription_plan_id);
                        return (
                          <div key={name} className={`rk-plan${featured ? ' featured' : ''}`}>
                            {featured && <span className="rk-plan-flag">{t('recommended_start')}</span>}
                            <div className="rk-plan-name">{name}</div>
                            {billingCycle === 'YEAR' && sv ? (
                              <div>
                                <div className="rk-plan-price">
                                  <s className="rk-plan-old">${sv.monthly}</s>
                                  <span className="rk-plan-arrow">→</span>
                                  ${sv.yearlyMonthly}
                                  <span className="rk-plan-save">−{sv.pct}%</span>
                                </div>
                                <div className="rk-plan-billed">{t('billing_per_month_billed_annually')}</div>
                              </div>
                            ) : (
                              <div className="rk-plan-price">${stripeDisplayPrice(row)}<span>{t('wxo_price_per_month')}</span></div>
                            )}
                            <div className="rk-plan-limits">
                              <div className="rk-plan-limit"><FontAwesomeIcon icon={faComments} /><span>{t('wxo_price_limit_messages')}</span><strong>{(mRow.message_limit ?? 0).toLocaleString('de-CH')}</strong></div>
                              <div className="rk-plan-limit"><FontAwesomeIcon icon={faBookOpen} /><span>{t('wxo_price_limit_knowledge')}</span><strong>{(mRow.document_limit ?? 0).toLocaleString('de-CH')}</strong></div>
                              <div className="rk-plan-limit"><FontAwesomeIcon icon={faPuzzlePiece} /><span>{t('wxo_price_limit_skills')}</span><strong>{mRow.skill_limit ?? 0}</strong></div>
                              <div className="rk-plan-limit"><FontAwesomeIcon icon={faSliders} /><span>{t('wxo_price_limit_behaviors')}</span><strong>{mRow.behavior_limit ?? 0}</strong></div>
                            </div>
                            <button type="button" className={`rk-plan-cta ${featured ? 'featured' : ''}`} disabled={isCheckingPlan || !canCheckout} onClick={() => startTrialCheckout(name)}>{t('start_free_trial')}</button>
                            <div className="rk-plan-div" />
                            <div className="rk-plan-sec"><span>{t('billing_section_apps')}</span></div>
                            <WpoLogoRow items={WPO_APPS} lead={WPO_APP_ANY} />
                            <div className="rk-plan-div" />
                            <div className="rk-plan-sec"><span>{t('billing_section_channels')}</span></div>
                            <WpoLogoRow items={WPO_CHANNELS} lead={WPO_CHANNEL_ANY} />
                            <div className="rk-plan-div" />
                            <ul className="rk-plan-feats">
                              <li><FontAwesomeIcon icon={faCheck} className="rk-feat-ok" /> {t('wxo_price_feat_lead')}</li>
                              <li className={rescrape !== 'none' ? '' : 'off'}><FontAwesomeIcon icon={rescrape !== 'none' ? faCheck : faMinus} className={rescrape !== 'none' ? 'rk-feat-ok' : ''} /> {rescrape === 'daily' ? t('wxo_price_feat_rescrape_daily') : rescrape === 'monthly' ? t('wxo_price_feat_rescrape_monthly') : t('wxo_price_feat_rescrape')}</li>
                              <li className={removeBranding ? '' : 'off'}><FontAwesomeIcon icon={removeBranding ? faCheck : faMinus} className={removeBranding ? 'rk-feat-ok' : ''} /> {t('wxo_price_feat_branding')}</li>
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {err && <div className="wxo-err">{err}</div>}
                  <p className="wxo-price-note">{t('cancel_note')}</p>
                  <p className="wxo-price-free"><button type="button" onClick={goToPortal}>{t('wxo_price_free_link')}</button></p>
                </>
              )}
            </div>
          )}

          {stageCta && (
            <div className="wxo-stage-cta">
              <button type="button" className="wxo-cta-below" onClick={stageCta.onClick} disabled={stageCta.busy}>
                <span>{stageCta.label}</span>
                {stageCta.busy ? <span className="wxo-spin" /> : <FontAwesomeIcon icon={faArrowRight} />}
              </button>
            </div>
          )}

        </div>
      </div>

      <UploadFileModal
        open={docModalOpen}
        onClose={() => setDocModalOpen(false)}
        onSubmit={handleDocsAdded}
      />

      {liveModalOpen && (
        <div className="wxo-live-modal-overlay" onClick={() => setLiveModalOpen(false)}>
          <div className="wxo-live-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="wxo-skills-grid-bg" aria-hidden="true" />
            <div className="wxo-skills-glow" aria-hidden="true" />
            <button type="button" className="wxo-live-modal-close" onClick={() => setLiveModalOpen(false)} aria-label={t('close')}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <div className="wxo-live-modal-inner">
              <span className="wxo-live-modal-icon"><FontAwesomeIcon icon={faHeadset} /></span>
              <h3 className="wxo-live-modal-title">{t('wxo_live_modal_title')}</h3>
              <p className="wxo-live-modal-body">{t('wxo_live_modal_body')}</p>
              <button type="button" className="wxo-live-modal-ok" onClick={() => setLiveModalOpen(false)}>{t('wxo_live_modal_ok')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
